import { useRef, useState } from "react";
import { UploadCloud, FileAudio, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { MeetingForm, useMeetingForm, useSubmitMeeting } from "@/components/app/MeetingForm";
import { PageHeader, Card } from "@/components/app/StatusBadge";
import { FreeLimitBanner } from "@/components/billing/FreeLimitBanner";
import { useApp } from "@/lib/i18n";
import { shouldCompress } from "@/lib/compressAudio";
import { useAuth } from "@/context/AuthContext";
import { PendingUploads } from "@/components/app/PendingUploads";

const EXT = ["mp3", "wav", "m4a", "webm", "mp4", "mov", "ogg"];

export default function UploadPage() {
  const { L, config } = useApp();
  const { workspace } = useAuth();
  const plan = config.plans?.find((p) => p.key === (workspace?.plan || "free")) || {};
  const MAX_MB = plan.max_upload_mb || 50;
  const maxH = Math.round((plan.max_duration_min || 30) / 60 * 10) / 10;
  const [meta, set] = useMeetingForm();
  const { submit, busy, bar } = useSubmitMeeting();
  const [file, setFile] = useState(null);
  const [drag, setDrag] = useState(false);
  const input = useRef(null);

  const pick = (f) => {
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!EXT.includes(ext)) return toast.error(L(`Format tidak disokong. Gunakan ${EXT.join(", ")}`, `Unsupported format. Use ${EXT.join(", ")}`));
    if (f.size > MAX_MB * 1048576 && !shouldCompress(f, f.name)) return toast.error(L(`Saiz melebihi had pelan ${plan.name || ""} (${MAX_MB} MB). Naik taraf untuk fail lebih besar.`, `File exceeds your plan limit (${MAX_MB} MB). Upgrade for larger files.`));
    setFile(f);
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-8" data-testid="upload-page">
      <PageHeader title={L("Muat Naik Rakaman", "Upload Recording")} desc={L(`Audio atau video: ${EXT.join(", ").toUpperCase()} · pelan ${plan.name || ""}: maks ${MAX_MB} MB & ${maxH >= 1 ? `${maxH} jam` : `${plan.max_duration_min || 30} minit`} setiap fail. Fail besar dimampatkan automatik sebelum dihantar.`, `Audio or video: ${EXT.join(", ").toUpperCase()} · ${plan.name || ""} plan: max ${MAX_MB} MB & ${maxH >= 1 ? `${maxH} h` : `${plan.max_duration_min || 30} min`} per file. Large files are compressed automatically before sending.`)} />
      <FreeLimitBanner />
      <PendingUploads file={file} busy={busy} />
      <Card className="mt-6 p-6">
        <div
          role="button"
          tabIndex={0}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && input.current.click()}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files[0]); }}
          onClick={() => input.current.click()}
          data-testid="upload-dropzone"
          className={`grid cursor-pointer place-items-center rounded-xl border-2 border-dashed p-10 text-center transition-colors ${drag ? "border-violet-500 bg-violet-50" : "border-slate-300 hover:border-violet-400"}`}
        >
          <UploadCloud size={30} className="text-violet-600" />
          <p className="mt-3 font-semibold">{L("Seret & lepas fail di sini", "Drag & drop a file here")}</p>
          <p className="mt-1 text-sm text-slate-500">{L("atau klik untuk memilih fail", "or click to choose a file")}</p>
          <input ref={input} type="file" hidden accept={EXT.map((e) => `.${e}`).join(",") + ",audio/*,video/*"} onChange={(e) => pick(e.target.files[0])} data-testid="upload-file-input" />
        </div>
        {file && (
          <div className="mt-4 flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 text-sm" data-testid="upload-selected-file">
            <span className="flex items-center gap-2 truncate"><FileAudio size={16} /> {file.name} <span className="text-slate-400">({(file.size / 1048576).toFixed(1)} MB)</span></span>
            <button onClick={() => setFile(null)} aria-label="Buang fail" data-testid="upload-clear-btn"><X size={16} /></button>
          </div>
        )}
        {bar}
      </Card>
      <Card className="mt-6 p-6">
        <h2 className="mb-4 font-semibold">{L("Butiran mesyuarat", "Meeting details")}</h2>
        <MeetingForm meta={meta} set={set} />
        <Button disabled={!file || busy} onClick={() => submit(file, file.name, meta, "upload")} className="mt-6 h-11 rounded-xl bg-violet-600 px-6 hover:bg-violet-700" data-testid="upload-submit-btn">
          {L("Muat naik & proses", "Upload & process")}
        </Button>
      </Card>
    </div>
  );
}
